import React from "react";
import { useTranslation } from "react-i18next";

import { formatEmptyPrice, SpecFormatter } from "../../../../commons/libs/formatters";
import { getMaxDiscountPercentage, isProduct } from "../../../../commons/libs/specs";
import { Product, ProductSpecKey, ProductType, StandaloneProductVariant } from "../../../../commons/specs/product";
import { Currency } from "../../../../commons/types/currency";
import { AssortmentPriceSettings, CustomizationSettings } from "../../../../commons/types/settings";
import Badge from "../../components/Badge/Badge";

interface SharedProps<Type extends ProductType> {
  productOrVariant?: Product<Type> | StandaloneProductVariant<Type>;
  specFormatter?: SpecFormatter<Type>;
  customization: CustomizationSettings;
}

const getDiscountPercentage = <Type extends ProductType>(
  productOrVariant?: Product<Type> | StandaloneProductVariant<Type>
): number | undefined => {
  if (productOrVariant && isProduct(productOrVariant)) {
    return getMaxDiscountPercentage(productOrVariant);
  } else {
    return productOrVariant?.[ProductSpecKey.DiscountPercentage] ?? undefined;
  }
};

export const PricePartial = <Type extends ProductType>({
  productOrVariant,
  customization,
  currency,
  specFormatter,
  inSpecView = false,
  forceShow = false
}: {
  inSpecView?: boolean;
  forceShow?: boolean;
  currency: Currency;
} & SharedProps<Type>) => {
  const { i18n } = useTranslation(["commons"]);

  if (productOrVariant && specFormatter) {
    const shouldShowPrice = inSpecView
      ? customization.showPrices || customization.showPricesFeaturesOnly
      : customization.showPrices;

    if (forceShow || shouldShowPrice) {
      const price = specFormatter.formatProductOrVariantValue(ProductSpecKey.Price, productOrVariant);
      // In case that there is no price available, we want to explicitly show an empty price, instead of showing nothing.
      return price ? price : formatEmptyPrice(currency, i18n.language);
    }
  }
};

export const OriginalPricePartial = <Type extends ProductType>({
  productOrVariant,
  customization,
  assortmentPriceSettings,
  specFormatter,
  inSpecView = false
}: {
  assortmentPriceSettings: AssortmentPriceSettings;
  inSpecView?: boolean;
} & SharedProps<Type>) => {
  if (productOrVariant && specFormatter) {
    const shouldShowPrice = inSpecView
      ? customization.showPrices || customization.showPricesFeaturesOnly
      : customization.showPrices;

    if (shouldShowPrice && assortmentPriceSettings.showOriginalPrices && getDiscountPercentage(productOrVariant)) {
      return specFormatter.formatProductOrVariantValue(ProductSpecKey.OriginalPrice, productOrVariant);
    }
  }
};

export const SaleBadgePartial = <Type extends ProductType>({
  productOrVariant,
  customization,
  assortmentPriceSettings,
  specFormatter,
  size = "s"
}: {
  assortmentPriceSettings: AssortmentPriceSettings;
  size?: React.ComponentProps<typeof Badge>["size"];
} & SharedProps<Type>) => {
  const { t } = useTranslation(["commons"]);

  if (productOrVariant && specFormatter) {
    if (customization.showPrices && assortmentPriceSettings.showSaleBadge && getDiscountPercentage(productOrVariant)) {
      switch (assortmentPriceSettings.showSaleBadge) {
        case "word":
          return <Badge label={t("commons:defaults.sale")} size={size} />;
        case "percentage":
          return (
            <Badge
              label={specFormatter.formatProductOrVariantValue(ProductSpecKey.DiscountPercentage, productOrVariant)}
              size={size}
            />
          );
        default:
          return undefined;
      }
    }
  }
};
